// SOURCE OF TRUTH: apps/selfservice/src/components/self-service/shared/rotationSegments.ts — keep byte-identical.
// Mirrored at selfservice-api/src/shared/rotation-segments.ts and
// selfservice-worker/src/shared/rotation-segments.ts. See ADR-0007 (CTV-476).
//
// Pure functions, zero dependencies. All date strings are YYYY-MM-DD and
// compared lexicographically; UTC math is used for boundary arithmetic.

export interface SegmentShare {
  startDate: string;
  endDate: string;
  share: number;
}

export interface CreativeWindow {
  id: string;
  startDate: string;
  endDate: string;
  segmentShares: SegmentShare[];
}

export interface RotationSegment {
  startDate: string;
  endDate: string;
  eligible: Array<{ creativeId: string; share: number }>;
}

const DAY_MS = 86_400_000;

function parseDate(value: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).slice(0, 10));
  if (!m) return NaN;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return NaN;
  }
  return t;
}

function formatDate(t: number): string {
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function addDays(date: string, n: number): string {
  return formatDate(parseDate(date) + n * DAY_MS);
}

function unionWindow(creatives: CreativeWindow[]): { start: string; end: string } | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const c of creatives) {
    const s = parseDate(c.startDate);
    const e = parseDate(c.endDate);
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
    if (s < lo) lo = s;
    if (e > hi) hi = e;
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  return { start: formatDate(lo), end: formatDate(hi) };
}

/**
 * Union all creative-window and segment_share endpoints (clipped to the
 * given window) into a partition of maximal non-overlapping Rotation
 * Segments. For each segment, list every creative whose window covers the
 * segment along with that creative's segment_share value for the range.
 *
 * `eligible` retains the input creative order so colors stay stable.
 */
export function computeRotationSegments(
  creatives: CreativeWindow[],
  windowStart: string,
  windowEnd: string,
): RotationSegment[] {
  const wStart = parseDate(windowStart);
  const wEnd = parseDate(windowEnd);
  if (!Number.isFinite(wStart) || !Number.isFinite(wEnd) || wEnd < wStart) return [];

  const boundaries = new Set<number>();
  boundaries.add(wStart);
  boundaries.add(wEnd + DAY_MS);

  for (const c of creatives) {
    const cs = parseDate(c.startDate);
    const ce = parseDate(c.endDate);
    if (Number.isFinite(cs) && cs > wStart && cs <= wEnd) boundaries.add(cs);
    if (Number.isFinite(ce) && ce >= wStart && ce < wEnd) boundaries.add(ce + DAY_MS);
    for (const ss of c.segmentShares) {
      const ssStart = parseDate(ss.startDate);
      const ssEnd = parseDate(ss.endDate);
      if (Number.isFinite(ssStart) && ssStart > wStart && ssStart <= wEnd) boundaries.add(ssStart);
      if (Number.isFinite(ssEnd) && ssEnd >= wStart && ssEnd < wEnd) boundaries.add(ssEnd + DAY_MS);
    }
  }

  const sorted = [...boundaries].sort((a, b) => a - b);
  const segments: RotationSegment[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const segStartT = sorted[i];
    const segEndT = sorted[i + 1] - DAY_MS;
    if (segEndT < segStartT) continue;

    const eligible: Array<{ creativeId: string; share: number }> = [];
    for (const c of creatives) {
      const cs = parseDate(c.startDate);
      const ce = parseDate(c.endDate);
      if (!Number.isFinite(cs) || !Number.isFinite(ce)) continue;
      if (cs > segStartT || ce < segEndT) continue;
      let share: number | null = null;
      for (const ss of c.segmentShares) {
        const ssStart = parseDate(ss.startDate);
        const ssEnd = parseDate(ss.endDate);
        if (Number.isFinite(ssStart) && Number.isFinite(ssEnd) && ssStart <= segStartT && ssEnd >= segEndT) {
          share = ss.share;
          break;
        }
      }
      if (share == null) continue;
      eligible.push({ creativeId: c.id, share });
    }
    if (eligible.length === 0) continue;
    segments.push({
      startDate: formatDate(segStartT),
      endDate: formatDate(segEndT),
      eligible,
    });
  }
  return segments;
}

function evenShares(n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [100];
  const base = Math.floor(100 / n);
  const out = new Array<number>(n).fill(base);
  out[0] += 100 - base * n;
  return out;
}

function normalizeShares(shares: number[]): number[] {
  if (shares.length === 0) return [];
  if (shares.length === 1) return [100];
  const sum = shares.reduce((a, b) => a + b, 0);
  if (sum <= 0) return evenShares(shares.length);
  const scaled = shares.map((s) => Math.max(1, Math.floor((s * 100) / sum)));
  const scaledSum = scaled.reduce((a, b) => a + b, 0);
  if (scaledSum !== 100) {
    const largestIdx = scaled.reduce((maxI, v, i) => (v > scaled[maxI] ? i : maxI), 0);
    scaled[largestIdx] += 100 - scaledSum;
  }
  return scaled;
}

/**
 * Read off a creative's segment_shares partition from a list of Rotation
 * Segments. Adjacent identical-share elements are merged so the resulting
 * array is the minimal partition of the creative's own window.
 */
function creativeSegmentSharesFromSegments(
  creativeId: string,
  segments: RotationSegment[],
): SegmentShare[] {
  const out: SegmentShare[] = [];
  for (const seg of segments) {
    const e = seg.eligible.find((x) => x.creativeId === creativeId);
    if (!e) continue;
    const last = out[out.length - 1];
    if (last && addDays(last.endDate, 1) === seg.startDate && last.share === e.share) {
      last.endDate = seg.endDate;
    } else {
      out.push({ startDate: seg.startDate, endDate: seg.endDate, share: e.share });
    }
  }
  return out;
}

/**
 * Re-derive shares after one creative's date window changed, per the
 * Rotation Segments rules:
 *
 *   • same eligible set, just resized: carry typed shares
 *   • new eligible set (a creative joined): default to even split
 *   • eligible set shrinks (a creative left): re-normalize remaining shares
 *
 * Boundary heuristic: each new segment is matched to the old segment that
 * covers its start day. Segments outside any old segment default to even.
 */
export function rederiveSegmentSharesOnDateChange(
  creatives: CreativeWindow[],
  changedCreativeId: string,
  newStartDate: string,
  newEndDate: string,
): CreativeWindow[] {
  const oldWin = unionWindow(creatives);
  const oldSegments = oldWin
    ? computeRotationSegments(creatives, oldWin.start, oldWin.end)
    : [];

  const updated: CreativeWindow[] = creatives.map((c) =>
    c.id === changedCreativeId
      ? {
          ...c,
          startDate: newStartDate,
          endDate: newEndDate,
          segmentShares: [{ startDate: newStartDate, endDate: newEndDate, share: 1 }],
        }
      : c,
  );
  const newWin = unionWindow(updated);
  if (!newWin) return updated;
  const newSegments = computeRotationSegments(updated, newWin.start, newWin.end);

  const adjusted = newSegments.map((seg) => {
    const segStartT = parseDate(seg.startDate);
    const old = oldSegments.find((o) => {
      const os = parseDate(o.startDate);
      const oe = parseDate(o.endDate);
      return Number.isFinite(os) && Number.isFinite(oe) && os <= segStartT && oe >= segStartT;
    });
    const newIds = seg.eligible.map((e) => e.creativeId);

    if (!old) {
      const evens = evenShares(seg.eligible.length);
      return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: evens[i] })) };
    }
    const oldIds = old.eligible.map((e) => e.creativeId);
    const oldSet = new Set(oldIds);
    const same = newIds.length === oldIds.length && newIds.every((id) => oldSet.has(id));
    const subset = newIds.every((id) => oldSet.has(id));

    if (same) {
      const lookup = new Map(old.eligible.map((e) => [e.creativeId, e.share]));
      return {
        ...seg,
        eligible: seg.eligible.map((e) => ({ ...e, share: lookup.get(e.creativeId) ?? 0 })),
      };
    }
    if (subset) {
      const lookup = new Map(old.eligible.map((e) => [e.creativeId, e.share]));
      const raw = seg.eligible.map((e) => lookup.get(e.creativeId) ?? 0);
      const normalized = normalizeShares(raw);
      return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: normalized[i] })) };
    }
    const evens = evenShares(seg.eligible.length);
    return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: evens[i] })) };
  });

  return updated.map((c) => ({
    ...c,
    segmentShares: creativeSegmentSharesFromSegments(c.id, adjusted),
  }));
}

/**
 * Pair-shift edit: in one Rotation Segment, the left creative's share becomes
 * `newLeftShare` and the right creative's share becomes `(left+right) -
 * newLeftShare`. Every other creative's share in the segment is left
 * untouched. This is the divider-drag semantics from the legacy global
 * rotation bar — dragging the boundary between two slices keeps every other
 * slice's width pinned.
 *
 * The new left share is clamped to [1, combined-1] so neither side falls
 * below 1%.
 */
export function applyPairShift(
  creatives: CreativeWindow[],
  segment: { startDate: string; endDate: string },
  leftCreativeId: string,
  rightCreativeId: string,
  newLeftShare: number,
): CreativeWindow[] {
  const win = unionWindow(creatives);
  if (!win) return creatives;
  const segments = computeRotationSegments(creatives, win.start, win.end);

  const updatedSegments = segments.map((seg) => {
    if (seg.startDate !== segment.startDate || seg.endDate !== segment.endDate) return seg;
    const left = seg.eligible.find((e) => e.creativeId === leftCreativeId);
    const right = seg.eligible.find((e) => e.creativeId === rightCreativeId);
    if (!left || !right) return seg;
    const combined = left.share + right.share;
    if (combined < 2) return seg;
    const clampedLeft = Math.max(1, Math.min(combined - 1, Math.round(newLeftShare)));
    const newRight = combined - clampedLeft;
    return {
      ...seg,
      eligible: seg.eligible.map((e) => {
        if (e.creativeId === leftCreativeId) return { ...e, share: clampedLeft };
        if (e.creativeId === rightCreativeId) return { ...e, share: newRight };
        return e;
      }),
    };
  });

  return creatives.map((c) => ({
    ...c,
    segmentShares: creativeSegmentSharesFromSegments(c.id, updatedSegments),
  }));
}

/**
 * Recency-aware share edit for the typed-input path (numeric input under the
 * Segment editor strip). Previously-edited creatives in the same segment are
 * "kept" — their shares stay frozen — unless honoring them would leave the
 * remaining pool with less than 1% per slot. When that happens we drop
 * oldest kept entries one at a time until the pool can satisfy its minimum.
 *
 * The pool (creatives not in the kept set) splits the remaining budget
 * evenly with a 1% floor. The just-edited creative is always at the end of
 * the kept stack — re-editing the same creative moves it to "most recent"
 * without growing the stack.
 *
 * Returns the updated creatives AND the new kept-stack (oldest first) so
 * the caller can persist it across calls.
 */
export function applySegmentShareEditWithRecency(
  creatives: CreativeWindow[],
  segment: { startDate: string; endDate: string },
  creativeId: string,
  newShare: number,
  prevEditedOrder: string[],
): { creatives: CreativeWindow[]; editedOrder: string[] } {
  const win = unionWindow(creatives);
  if (!win) return { creatives, editedOrder: prevEditedOrder };
  const segments = computeRotationSegments(creatives, win.start, win.end);

  let finalEditedOrder: string[] = prevEditedOrder.slice();

  const updatedSegments = segments.map((seg) => {
    if (seg.startDate !== segment.startDate || seg.endDate !== segment.endDate) return seg;
    if (seg.eligible.length < 2) return seg;
    if (!seg.eligible.find((e) => e.creativeId === creativeId)) return seg;

    const eligibleIds = new Set(seg.eligible.map((e) => e.creativeId));
    const maxAllowed = 100 - (seg.eligible.length - 1);
    const clamped = Math.max(1, Math.min(maxAllowed, Math.round(newShare)));

    // kept = previous-edits filtered to currently-eligible (and excluding the
    // just-edited id) + the just-edited id appended as most-recent.
    const kept = prevEditedOrder.filter((id) => id !== creativeId && eligibleIds.has(id));
    kept.push(creativeId);

    const sharesByCreative = new Map<string, number>(
      seg.eligible.map((e) => [e.creativeId, e.share]),
    );
    sharesByCreative.set(creativeId, clamped);

    const totalSlots = seg.eligible.length;

    // Drop oldest kept entries until the pool can satisfy 1% per slot.
    while (kept.length > 1) {
      const keptSum = kept.reduce((s, id) => s + (sharesByCreative.get(id) ?? 0), 0);
      const poolSize = totalSlots - kept.length;
      const minPoolBudget = poolSize; // 1% per pool slot
      if (keptSum + minPoolBudget <= 100) break;
      kept.shift();
    }

    finalEditedOrder = kept.slice();

    const poolIds = seg.eligible
      .map((e) => e.creativeId)
      .filter((id) => !kept.includes(id));
    const keptSum = kept.reduce((s, id) => s + (sharesByCreative.get(id) ?? 0), 0);
    const remaining = Math.max(0, 100 - keptSum);

    if (poolIds.length === 0) {
      // Every slot is kept. Hold the just-edited at `clamped`; proportionally
      // scale the other kept slots so the total lands at 100.
      const others = kept.filter((id) => id !== creativeId);
      const otherSum = others.reduce((s, id) => s + (sharesByCreative.get(id) ?? 0), 0);
      const targetForOthers = 100 - clamped;
      if (others.length > 0 && targetForOthers > 0) {
        if (otherSum > 0) {
          const scaled = others.map((id) =>
            Math.max(1, Math.floor(((sharesByCreative.get(id) ?? 0) * targetForOthers) / otherSum)),
          );
          const scaledSum = scaled.reduce((a, b) => a + b, 0);
          if (scaledSum !== targetForOthers) scaled[0] += targetForOthers - scaledSum;
          others.forEach((id, i) => sharesByCreative.set(id, Math.max(1, scaled[i])));
        } else {
          const evens = evenShares(others.length);
          const factor = targetForOthers / 100;
          const scaled = evens.map((v) => Math.max(1, Math.floor(v * factor)));
          const scaledSum = scaled.reduce((a, b) => a + b, 0);
          if (scaledSum !== targetForOthers) scaled[0] += targetForOthers - scaledSum;
          others.forEach((id, i) => sharesByCreative.set(id, Math.max(1, scaled[i])));
        }
      }
    } else {
      // Distribute `remaining` evenly across the pool with a 1% floor.
      const base = Math.floor(remaining / poolIds.length);
      const extra = remaining - base * poolIds.length;
      poolIds.forEach((id, i) => {
        sharesByCreative.set(id, Math.max(1, base + (i === 0 ? extra : 0)));
      });
    }

    return {
      ...seg,
      eligible: seg.eligible.map((e) => ({
        ...e,
        share: sharesByCreative.get(e.creativeId) ?? e.share,
      })),
    };
  });

  return {
    creatives: creatives.map((c) => ({
      ...c,
      segmentShares: creativeSegmentSharesFromSegments(c.id, updatedSegments),
    })),
    editedOrder: finalEditedOrder,
  };
}

/**
 * Reset every overlap Rotation Segment to even split. Solo segments stay
 * at 100% (evenShares(1) = [100]) and date windows are untouched.
 */
export function resetToEven(creatives: CreativeWindow[]): CreativeWindow[] {
  const win = unionWindow(creatives);
  if (!win) return creatives;
  const segments = computeRotationSegments(creatives, win.start, win.end);
  const updated = segments.map((seg) => {
    const evens = evenShares(seg.eligible.length);
    return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: evens[i] })) };
  });
  return creatives.map((c) => ({
    ...c,
    segmentShares: creativeSegmentSharesFromSegments(c.id, updated),
  }));
}

/**
 * Append a new creative and default its share in every overlap segment it
 * now joins to an even split, re-normalizing existing creatives' shares so
 * each segment still sums to 100.
 */
export function seedSegmentSharesForNewCreative(
  existing: CreativeWindow[],
  newCreative: { id: string; startDate: string; endDate: string },
): CreativeWindow[] {
  const prospective: CreativeWindow[] = [
    ...existing,
    {
      id: newCreative.id,
      startDate: newCreative.startDate,
      endDate: newCreative.endDate,
      segmentShares: [
        { startDate: newCreative.startDate, endDate: newCreative.endDate, share: 1 },
      ],
    },
  ];
  const win = unionWindow(prospective);
  if (!win) return prospective;
  const newSegs = computeRotationSegments(prospective, win.start, win.end);

  const adjusted = newSegs.map((seg) => {
    const hasNew = seg.eligible.some((e) => e.creativeId === newCreative.id);
    if (hasNew) {
      const evens = evenShares(seg.eligible.length);
      return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: evens[i] })) };
    }
    const normalized = normalizeShares(seg.eligible.map((e) => e.share));
    return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: normalized[i] })) };
  });

  return prospective.map((c) => ({
    ...c,
    segmentShares: creativeSegmentSharesFromSegments(c.id, adjusted),
  }));
}

/**
 * After removing a creative, re-normalize each segment's remaining shares.
 */
export function reseatSharesAfterRemoval(creatives: CreativeWindow[]): CreativeWindow[] {
  const win = unionWindow(creatives);
  if (!win) return creatives;
  const segments = computeRotationSegments(creatives, win.start, win.end);
  const adjusted = segments.map((seg) => {
    const normalized = normalizeShares(seg.eligible.map((e) => e.share));
    return { ...seg, eligible: seg.eligible.map((e, i) => ({ ...e, share: normalized[i] })) };
  });
  return creatives.map((c) => ({
    ...c,
    segmentShares: creativeSegmentSharesFromSegments(c.id, adjusted),
  }));
}

/**
 * Render the Schedule Dates Weight-column summary: comma-separated shares
 * across the creative's own segment_shares (`100% · 50% · 100%`).
 */
export function formatSegmentSharesSummary(c: CreativeWindow): string {
  return c.segmentShares.map((s) => `${s.share}%`).join(' · ');
}

/**
 * Make a single-element segment_shares array that covers a creative's full
 * window at 100% — the default for a newly added solo creative, or for a
 * creative whose siblings happen to never overlap it.
 */
export function singleSegmentShareForWindow(startDate: string, endDate: string): SegmentShare[] {
  return [{ startDate, endDate, share: 100 }];
}

/**
 * Validate that a creative's segment_shares partition its [startDate,
 * endDate] window with no gaps and no overlaps. Used by server-side
 * validation as a defense in depth — the wizard maintains the invariant by
 * construction.
 */
export function isSegmentSharesPartition(c: CreativeWindow): boolean {
  if (c.segmentShares.length === 0) return false;
  const sorted = [...c.segmentShares].sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
  if (sorted[0].startDate !== c.startDate) return false;
  if (sorted[sorted.length - 1].endDate !== c.endDate) return false;
  for (let i = 0; i < sorted.length; i++) {
    const e = sorted[i];
    if (e.endDate < e.startDate) return false;
    if (e.share < 0 || e.share > 100) return false;
    if (i > 0 && addDays(sorted[i - 1].endDate, 1) !== e.startDate) return false;
  }
  return true;
}
